import apiClient from './client'
import JSEncrypt from 'jsencrypt'

export interface AdminUser {
  id: string
  name: string
  email: string
  role?: string
  role_name?: string
  permissions?: string[]
  [key: string]: any
}

export interface LoginResponse {
  success: boolean
  message?: string
  requires_2fa?: boolean
  temp_token?: string
  admin_id?: string
  access_token?: string
  admin?: AdminUser
}

let cachedPublicKey: { publicKey: string; nonce: string } | null = null
let keyExpiresAt = 0

export async function getEncryptionKey(): Promise<{ publicKey: string; nonce: string }> {
  if (cachedPublicKey && Date.now() < keyExpiresAt) {
    return cachedPublicKey
  }
  const response = await apiClient.get('/auth/encryption-key')
  if (response.data && response.data.success && response.data.data) {
    cachedPublicKey = {
      publicKey: response.data.data.public_key,
      nonce: response.data.data.nonce
    }
    keyExpiresAt = Date.now() + 50 * 60 * 1000 // 50 minutes cache
    return cachedPublicKey
  }
  throw new Error(response.data?.message || 'Failed to fetch encryption key')
}

export async function encryptPassword(password: string): Promise<string> {
  const { publicKey } = await getEncryptionKey()
  const encryptor = new JSEncrypt()
  encryptor.setPublicKey(publicKey)
  const encrypted = encryptor.encrypt(password)
  if (!encrypted) {
    throw new Error('RSA encryption failed')
  }
  return encrypted
}

export async function loginWithCredentials(
  email: string,
  encryptedPassword: string,
  secretCode?: string
): Promise<LoginResponse> {
  const payload: Record<string, any> = {
    email,
    password: encryptedPassword
  }
  if (secretCode) {
    payload.secret_code = secretCode
  }

  const response = await apiClient.post('/admin/login', payload)
  return response.data
}

export async function verify2FA(tempToken: string, code: string): Promise<LoginResponse> {
  const response = await apiClient.post('/admin/auth/2fa/verify', {
    temp_token: tempToken,
    code
  })
  return response.data
}

export async function fetchAdminMe(token?: string): Promise<{ success: boolean; data: AdminUser }> {
  const response = await apiClient.get('/admin/me', token ? {
    headers: { Authorization: `Bearer ${token}` }
  } : undefined)
  const result = response.data
  if (result?.success !== true || typeof result.data?.id !== 'string' || !result.data.id) {
    throw new Error('Unable to verify your admin account. Please try again.')
  }
  return result
}

function getKeycloakRedirectURI(): string {
  return import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/login`
}

export async function getKeycloakLoginURL(): Promise<string> {
  const baseURL = import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.talvexa.io'
  const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'talvexa'
  const clientID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'talvexa-backoffice-admin'
  const redirectURI = encodeURIComponent(getKeycloakRedirectURI())
  return `${baseURL}/realms/${realm}/protocol/openid-connect/auth?client_id=${encodeURIComponent(clientID)}&redirect_uri=${redirectURI}&response_type=code&scope=openid`
}

export function parseJWTClaims(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (e) {
    console.error('Failed to parse JWT claims', e)
    return null
  }
}

export async function exchangeKeycloakCode(code: string): Promise<{
  success: boolean
  access_token: string
  admin: AdminUser
  refresh_token?: string
}> {
  const baseURL = import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.talvexa.io'
  const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'talvexa'
  const clientID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'talvexa-backoffice-admin'

  const redirect = getKeycloakRedirectURI()

  const tokenURL = `${baseURL}/realms/${realm}/protocol/openid-connect/token`

  const body = new URLSearchParams()
  body.append('grant_type', 'authorization_code')
  body.append('client_id', clientID)
  body.append('code', code)
  body.append('redirect_uri', redirect)

  const resp = await fetch(tokenURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: body.toString()
  })

  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({}))
    throw new Error(errBody.error_description || errBody.error || `Token exchange failed with status ${resp.status}`)
  }

  const tokenData = await resp.json()
  if (typeof tokenData.access_token !== 'string' || !tokenData.access_token) {
    throw new Error('Keycloak did not return an access token. Please sign in again.')
  }

  // The gateway validates the token and decides portal access, including mixed-role users.
  const { data: adminUser } = await fetchAdminMe(tokenData.access_token)

  return {
    success: true,
    access_token: tokenData.access_token,
    refresh_token: tokenData.refresh_token,
    admin: adminUser
  }
}

export function getKeycloakLogoutURL(): string {
  const baseURL = import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.talvexa.io'
  const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'talvexa'
  const clientID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'talvexa-backoffice-admin'
  const redirectURI = encodeURIComponent(`${window.location.origin}/login?local=true&error=access_denied`)
  return `${baseURL}/realms/${realm}/protocol/openid-connect/logout?client_id=${clientID}&post_logout_redirect_uri=${redirectURI}`
}


